import { useState, useEffect } from 'react';
import {
  Text, Button, Badge, Tag,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Input, Field,
} from '@fluentui/react-components';
import { useIntl } from 'react-intl';
import { ArrowLeftRegular, FolderRegular, DocumentRegular, ChatRegular, ClipboardRegular, ClipboardTaskListLtrRegular, MoneyRegular, ClipboardCheckmarkRegular, BuildingRegular, SlideTextRegular, ServerRegular, PeopleTeamRegular, AddRegular, EditRegular, DeleteRegular } from '@fluentui/react-icons';
import { CafPhaseTracker } from '../components/CafPhaseTracker';
import { updateProject, type Project, type EngagementTypeOption, type MigrateContext, type ProgrammeMember } from '../services/project-service';
import type { MethodologyDocument } from '../services/document-service';
import type { PresentationDocument } from '../services/presentation-service';
import { setMigrateContext, APPLIANCE_LADDER } from '../services/migrate-service';
import { nextRunDue } from '../utils/migrate-milestones';
import { showAzureMigratePanel, M365_ENGAGEMENT_TYPES } from '../utils/migrate-panel-gate';
import { useApiToken } from '../hooks/useApiToken';
import { auditControlCount } from './AuditNavigatorPage';
import { getBadgeProps, DOC_STATUS_BADGE, PROJECT_STATUS_BADGE } from '../utils/badge-config';

const PHASE_ORDER = ['governance', 'strategy', 'plan', 'ready', 'adopt', 'review', 'misc'];

interface ProjectDetailPageProps {
  project: Project;
  documents: MethodologyDocument[];
  presentations?: PresentationDocument[];
  engagementTypes?: EngagementTypeOption[];
  onBack: () => void;
  onSelectDocument: (doc: MethodologyDocument) => void;
  onSelectPresentation?: (deck: PresentationDocument) => void;
  onStartAssessment?: () => void;
  onOpenRegisters?: () => void;
  onOpenCostEstimate?: () => void;
  onOpenQuestionnaire?: () => void;
  onOpenAuditNavigator?: () => void;
  onOpenCustomerSettings?: () => void;
  /** Keeps App's in-memory project copy in sync after a server-side migrate-context write. */
  onMigrateContextChange?: (projectId: string, ctx: MigrateContext) => void;
  /** Keeps App's in-memory project copy in sync after a whole-project PATCH (programme members). */
  onProjectChange?: (project: Project) => void;
}

export function ProjectDetailPage({ project, documents, presentations = [], engagementTypes = [], onBack, onSelectDocument, onSelectPresentation, onStartAssessment, onOpenRegisters, onOpenCostEstimate, onOpenQuestionnaire, onOpenAuditNavigator, onOpenCustomerSettings, onMigrateContextChange, onProjectChange }: ProjectDetailPageProps) {
  const intl = useIntl();
  const engagementTypeLabel = (id: string) =>
    engagementTypes.find(t => t.id === id)?.displayName ?? id;
  // #412: Azure Migrate furniture is hidden for M365-only engagements (fail-open — see helper).
  const showMigrate = showAzureMigratePanel(project);
  // Programme members surface for M365-programme projects (epic #400 A3) — and for any
  // project that already carries members, so existing data never disappears.
  const showMembers =
    (project.engagementTypes ?? []).some(t => M365_ENGAGEMENT_TYPES.includes(t))
    || (project.programmeMembers?.length ?? 0) > 0;
  // Group documents by CAF phase
  const docsByPhase = PHASE_ORDER
    .map(phase => ({
      phase,
      docs: documents.filter(d => d.cafPhase === phase),
    }))
    .filter(g => g.docs.length > 0);

  return (
    <div className="page-root page-root--scrollable project-detail-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'projectDetail.btn.back.title' })} />
        <FolderRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{project.name}</Text>
        <div className="title-bar-actions">
          {onOpenRegisters && (
            <Button appearance="subtle" icon={<ClipboardRegular />} onClick={onOpenRegisters} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.registers' })}
            </Button>
          )}
          {onOpenCostEstimate && (
            <Button appearance="subtle" icon={<MoneyRegular />} onClick={onOpenCostEstimate} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.costEstimate' })}
            </Button>
          )}
          {onOpenQuestionnaire && (
            <Button appearance="subtle" icon={<ClipboardTaskListLtrRegular />} onClick={onOpenQuestionnaire} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.questionnaire' })}
            </Button>
          )}
          {onOpenAuditNavigator && (
            <Button appearance="subtle" icon={<ClipboardCheckmarkRegular />} onClick={onOpenAuditNavigator} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.auditNavigator' })}
            </Button>
          )}
          {onOpenCustomerSettings && (
            <Button appearance="subtle" icon={<BuildingRegular />} onClick={onOpenCustomerSettings} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.customerDetails' })}
            </Button>
          )}
          {onStartAssessment && (
            <Button appearance="primary" icon={<ChatRegular />} onClick={onStartAssessment} size="small">
              {intl.formatMessage({ id: 'projectDetail.btn.caseAssessment' })}
            </Button>
          )}
          <Badge size="small" {...getBadgeProps(PROJECT_STATUS_BADGE, project.status)}>{project.status}</Badge>
        </div>
      </div>

      <CafPhaseTracker currentPhase={project.cafPhase} documents={documents} />

      <div className="project-meta">
        <div className="meta-grid">
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.customer' })}</span><span>{project.customerId}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.scope' })}</span><span>{project.scope}</span></div>
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.region' })}</span><span>{project.azureRegion}</span></div>
          {showMigrate && (
            <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.hypervisor' })}</span><span>{project.hypervisor}</span></div>
          )}
          <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.documents' })}</span><span>{documents.length}</span></div>
          <div className="meta-item">
            <span className="text-muted">{intl.formatMessage({ id: 'projectDetail.meta.engagementType' })}</span>
            <span>
              {(project.engagementTypes ?? []).length === 0
                ? <span className="text-muted">—</span>
                : (
                  <span className="tag-chip-row">
                    {(project.engagementTypes ?? []).map(id => (
                      <Tag key={id} size="extra-small" appearance="brand">{engagementTypeLabel(id)}</Tag>
                    ))}
                  </span>
                )}
            </span>
          </div>
        </div>
      </div>

      {showMigrate && <ApplianceChecklistCard project={project} onMigrateContextChange={onMigrateContextChange} />}

      {showMembers && <ProgrammeMembersSection project={project} onProjectChange={onProjectChange} />}

      <div className="table-scroll-container">
        {docsByPhase.map(({ phase, docs }) => (
          <div key={phase} className="phase-group">
            <div className="section-header">
              <Text size={400} weight="semibold">{phase.charAt(0).toUpperCase() + phase.slice(1)}</Text>
              <Badge size="small" appearance="outline">{docs.length}</Badge>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.document' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.template' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.status' })}</th>
                  <th>{intl.formatMessage({ id: 'projectDetail.col.version' })}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  const ctrlCount = auditControlCount(doc);
                  return (
                    <tr key={doc.id} className="clickable" onClick={() => onSelectDocument(doc)}>
                      <td>
                        <div className="flex-row">
                          <DocumentRegular />
                          <Text weight="semibold">{doc.title}</Text>
                          <Badge
                            size="extra-small"
                            appearance="outline"
                            color={ctrlCount > 0 ? 'informative' : undefined}
                            title={ctrlCount === 0
                            ? intl.formatMessage({ id: 'projectDetail.auditBadge.titleNone' })
                            : intl.formatMessage({ id: 'projectDetail.auditBadge.titleCount' }, { count: ctrlCount })}
                          >
                            {intl.formatMessage({ id: 'projectDetail.auditBadge.label' }, { count: ctrlCount })}
                          </Badge>
                        </div>
                      </td>
                      <td className="text-muted control-ref">{doc.templateId}</td>
                      <td><Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, doc.status)}>{doc.status}</Badge></td>
                      <td className="text-muted">v{doc.version}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        {documents.length === 0 && (
          <div className="page-empty">
            <Text className="text-muted">{intl.formatMessage({ id: 'projectDetail.empty.documents' })}</Text>
          </div>
        )}
      </div>

      {/* Presentations */}
      {presentations.length > 0 && (
        <div className="presentations-section">
          <div className="section-header">
            <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.presentations.heading' })}</Text>
            <Badge size="small" appearance="outline">{presentations.length}</Badge>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.deck' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.session' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.template' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.slides' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.status' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.presentations.col.lastRendered' })}</th>
              </tr>
            </thead>
            <tbody>
              {[...presentations]
                .sort((a, b) => (a.sessionNumber ?? 999) - (b.sessionNumber ?? 999))
                .map(deck => (
                  <tr
                    key={deck.id}
                    className={onSelectPresentation ? 'clickable' : undefined}
                    onClick={() => onSelectPresentation?.(deck)}
                  >
                    <td>
                      <div className="flex-row">
                        <SlideTextRegular />
                        <Text weight="semibold">{deck.title}</Text>
                      </div>
                    </td>
                    <td className="text-muted">{deck.sessionNumber ? intl.formatMessage({ id: 'projectDetail.presentations.session' }, { number: deck.sessionNumber }) : '—'}</td>
                    <td className="text-muted control-ref">{deck.templateId}</td>
                    <td className="text-muted">{deck.slideCount ?? '—'}</td>
                    <td><Badge size="small" {...getBadgeProps(DOC_STATUS_BADGE, deck.status)}>{deck.status}</Badge></td>
                    <td className="text-muted">{deck.lastRenderedAt ? new Date(deck.lastRenderedAt).toLocaleString() : intl.formatMessage({ id: 'projectDetail.presentations.neverRendered' })}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Snapshots Timeline */}
      {project.snapshots && project.snapshots.length > 0 && (
        <div className="snapshots-section">
          <div className="section-header">
            <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.snapshots.heading' })}</Text>
            <Badge size="small" appearance="outline">{project.snapshots.length}</Badge>
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.version' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.label' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.date' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.workloads' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.facts' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.risks' })}</th>
                <th>{intl.formatMessage({ id: 'projectDetail.snapshots.col.estMonthly' })}</th>
              </tr>
            </thead>
            <tbody>
              {[...project.snapshots].reverse().map(s => (
                <tr key={s.version}>
                  <td><Badge size="small" appearance="outline">v{s.version}</Badge></td>
                  <td>{s.label}</td>
                  <td className="text-muted">{new Date(s.timestamp).toLocaleDateString()}</td>
                  <td>{s.workloadCount}</td>
                  <td>{s.factCount}</td>
                  <td>{s.riskCount}</td>
                  <td>{s.grandTotalMonthly ? `$${s.grandTotalMonthly.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Azure Migrate appliance checklist (orchestrator RFC §4.2/§4.6). The ladder is
 * tracked manually — appliance key generation has no REST surface — and the
 * advance button drives the server-side read-modify-write endpoint, never the
 * blind project PATCH. The nextRunDue chip arms once discovery starts.
 */
function ApplianceChecklistCard({ project, onMigrateContextChange }: {
  project: Project;
  onMigrateContextChange?: (projectId: string, ctx: MigrateContext) => void;
}) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [ctx, setCtx] = useState<MigrateContext | null>(project.migrateContext ?? null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => { setCtx(project.migrateContext ?? null); }, [project.id, project.migrateContext]);

  const status = ctx?.applianceStatus ?? 'not-started';
  const statusIndex = Math.max(0, APPLIANCE_LADDER.indexOf(status as typeof APPLIANCE_LADDER[number]));
  const nextStep = statusIndex < APPLIANCE_LADDER.length - 1 ? APPLIANCE_LADDER[statusIndex + 1] : null;
  const due = nextRunDue(ctx?.discoveryStartedAt, new Date());

  const advance = async () => {
    if (!project.id || !nextStep || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      const updated = await setMigrateContext(project.id, project.customerId, { applianceStatus: nextStep }, token);
      setCtx(updated);
      onMigrateContextChange?.(project.id, updated);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="project-meta">
      <div className="section-header">
        <ServerRegular className="icon-section text-brand" />
        <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.migrate.heading' })}</Text>
        {due && (
          <Badge size="small" appearance="tint" color={due.ready ? 'warning' : 'informative'}>
            {due.ready
              ? intl.formatMessage({ id: 'projectDetail.migrate.runReady' }, { timeRange: due.timeRange, since: due.dueAt.toLocaleDateString() })
              : intl.formatMessage({ id: 'projectDetail.migrate.runDue' }, { timeRange: due.timeRange, due: due.dueAt.toLocaleDateString() })}
          </Badge>
        )}
      </div>
      <div className="meta-grid">
        <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.migrate.assessmentProject' })}</span><span>{ctx?.assessmentProjectName ?? '—'}</span></div>
        <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.migrate.resourceGroup' })}</span><span>{ctx?.resourceGroup ?? '—'}</span></div>
        <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.migrate.appliance' })}</span><span>{ctx?.applianceName ?? '—'}</span></div>
        <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.migrate.authMode' })}</span><span>{ctx?.authMode ?? 'architect-workstation'}</span></div>
        <div className="meta-item"><span className="text-muted">{intl.formatMessage({ id: 'projectDetail.migrate.discoveryStarted' })}</span><span>{ctx?.discoveryStartedAt ? new Date(ctx.discoveryStartedAt).toLocaleDateString() : '—'}</span></div>
      </div>
      <div className="flex-row flex-wrap mt-sm">
        {APPLIANCE_LADDER.map((step, i) => (
          <Badge
            key={step}
            size="small"
            appearance={i === statusIndex ? 'filled' : 'tint'}
            color={i < statusIndex ? 'success' : i === statusIndex ? 'informative' : 'subtle'}
          >
            {step}
          </Badge>
        ))}
        {nextStep && (
          <Button size="small" appearance="subtle" disabled={saving} onClick={advance}>
            {intl.formatMessage({ id: 'projectDetail.migrate.advance' }, { next: nextStep })}
          </Button>
        )}
        {saveError && <Text className="text-danger">{saveError}</Text>}
      </div>
    </div>
  );
}

/**
 * Programme members (epic #400 A3) — the entities/tenants a group programme spans, each
 * with its wave, commercial disposition and in-scope window. An assignment register:
 * every member is assigned a wave and a window. Persistence is the whole-project PATCH
 * (full-doc read-modify-write via updateProject) — the same save path the app uses for
 * every project edit — with onProjectChange syncing App's in-memory copy.
 */
function ProgrammeMembersSection({ project, onProjectChange }: {
  project: Project;
  onProjectChange?: (project: Project) => void;
}) {
  const intl = useIntl();
  const getToken = useApiToken();
  const members = project.programmeMembers ?? [];
  // null = closed, -1 = add, >= 0 = index of the member being edited.
  const [dialogIndex, setDialogIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const persist = async (next: ProgrammeMember[]) => {
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      const updated = await updateProject({ ...project, programmeMembers: next }, token);
      onProjectChange?.(updated);
      setDialogIndex(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const saveMember = async (member: ProgrammeMember) => {
    const next = dialogIndex !== null && dialogIndex >= 0
      ? members.map((m, i) => (i === dialogIndex ? member : m))
      : [...members, member];
    await persist(next);
  };

  const removeMember = async (index: number) => {
    if (saving) return;
    await persist(members.filter((_, i) => i !== index));
  };

  return (
    <div className="project-meta">
      <div className="section-header">
        <PeopleTeamRegular className="icon-section text-brand" />
        <Text size={400} weight="semibold">{intl.formatMessage({ id: 'projectDetail.members.heading' })}</Text>
        <Badge size="small" appearance="outline">{members.length}</Badge>
        <Button size="small" appearance="subtle" icon={<AddRegular />} disabled={saving} onClick={() => setDialogIndex(-1)}>
          {intl.formatMessage({ id: 'projectDetail.members.btn.add' })}
        </Button>
        {saveError && <Text className="text-danger">{saveError}</Text>}
      </div>
      {members.length === 0 ? (
        <Text className="text-muted">{intl.formatMessage({ id: 'projectDetail.members.empty' })}</Text>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.member' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.tenantId' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.wave' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.disposition' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.inScopeFrom' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.inScopeTo' })}</th>
              <th>{intl.formatMessage({ id: 'projectDetail.members.col.actions' })}</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m, i) => (
              <tr key={`${m.displayName}-${i}`}>
                <td><Text weight="semibold">{m.displayName}</Text></td>
                <td className="text-muted control-ref">{m.tenantId || '—'}</td>
                <td><Badge size="small" appearance="outline">{intl.formatMessage({ id: 'projectDetail.members.wave' }, { wave: m.wave })}</Badge></td>
                <td className="text-muted">{m.commercialDisposition || '—'}</td>
                <td className="text-muted">{m.inScopeFrom ? m.inScopeFrom.split('T')[0] : '—'}</td>
                <td className="text-muted">{m.inScopeTo ? m.inScopeTo.split('T')[0] : '—'}</td>
                <td>
                  <div className="flex-row">
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<EditRegular />}
                      aria-label={intl.formatMessage({ id: 'projectDetail.members.btn.edit.ariaLabel' }, { name: m.displayName })}
                      disabled={saving}
                      onClick={() => setDialogIndex(i)}
                    />
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<DeleteRegular />}
                      aria-label={intl.formatMessage({ id: 'projectDetail.members.btn.remove.ariaLabel' }, { name: m.displayName })}
                      disabled={saving}
                      onClick={() => removeMember(i)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialogIndex !== null && (
        <ProgrammeMemberDialog
          member={dialogIndex >= 0 ? members[dialogIndex] : null}
          saving={saving}
          onSave={saveMember}
          onClose={() => setDialogIndex(null)}
        />
      )}
    </div>
  );
}

function ProgrammeMemberDialog({ member, saving, onSave, onClose }: {
  member: ProgrammeMember | null;
  saving: boolean;
  onSave: (member: ProgrammeMember) => Promise<void>;
  onClose: () => void;
}) {
  const intl = useIntl();
  const [form, setForm] = useState({
    displayName: member?.displayName ?? '',
    tenantId: member?.tenantId ?? '',
    wave: String(member?.wave ?? 1),
    commercialDisposition: member?.commercialDisposition ?? '',
    inScopeFrom: member?.inScopeFrom ? member.inScopeFrom.split('T')[0] : '',
    inScopeTo: member?.inScopeTo ? member.inScopeTo.split('T')[0] : '',
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const wave = Number.parseInt(form.wave, 10);
  const canSave = form.displayName.trim() !== '' && Number.isInteger(wave) && wave >= 1 && !saving;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      displayName: form.displayName.trim(),
      tenantId: form.tenantId.trim() || undefined,
      wave,
      commercialDisposition: form.commercialDisposition.trim() || undefined,
      inScopeFrom: form.inScopeFrom || undefined,
      inScopeTo: form.inScopeTo || undefined,
    });
  };

  return (
    <Dialog open onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {intl.formatMessage({ id: member ? 'projectDetail.members.dialog.edit.title' : 'projectDetail.members.dialog.add.title' })}
          </DialogTitle>
          <DialogContent>
            <div className="dialog-form">
              <Field label={intl.formatMessage({ id: 'projectDetail.members.field.displayName' })} required>
                <Input value={form.displayName} onChange={(_e, d) => set('displayName', d.value)} />
              </Field>
              <Field
                label={intl.formatMessage({ id: 'projectDetail.members.field.tenantId' })}
                hint={intl.formatMessage({ id: 'projectDetail.members.field.tenantId.hint' })}
              >
                <Input value={form.tenantId} onChange={(_e, d) => set('tenantId', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'projectDetail.members.field.wave' })} required>
                <Input type="number" min={1} value={form.wave} onChange={(_e, d) => set('wave', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'projectDetail.members.field.disposition' })}>
                <Input
                  value={form.commercialDisposition}
                  placeholder={intl.formatMessage({ id: 'projectDetail.members.field.disposition.placeholder' })}
                  onChange={(_e, d) => set('commercialDisposition', d.value)}
                />
              </Field>
              <Field label={intl.formatMessage({ id: 'projectDetail.members.field.inScopeFrom' })}>
                <Input type="date" value={form.inScopeFrom} onChange={(_e, d) => set('inScopeFrom', d.value)} />
              </Field>
              <Field label={intl.formatMessage({ id: 'projectDetail.members.field.inScopeTo' })}>
                <Input type="date" value={form.inScopeTo} onChange={(_e, d) => set('inScopeTo', d.value)} />
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {intl.formatMessage({ id: 'projectDetail.members.btn.cancel' })}
            </Button>
            <Button appearance="primary" onClick={handleSave} disabled={!canSave}>
              {saving
                ? intl.formatMessage({ id: 'projectDetail.members.btn.saving' })
                : intl.formatMessage({ id: 'projectDetail.members.btn.save' })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
